export const translations = {
  en: {
    nav: {
      home: 'Home',
      steps: 'Steps',
      differentiation: 'Niche Finder',
      contact: 'Contact',
      login: 'Login',
      logout: 'Logout',
      getStarted: 'Start for free'
    },
    hero: {
      subtitle: 'The 10-step methodology to transform your idea into a market-ready product.',
      subtitleHighlight: 'Structured. Proven. Effective.',
      ctaPrimary: 'Start for free',
      ctaSecondary: 'Discover More',
      feature1: 'Structured Process',
      feature1Desc: 'Follow a proven 10-step plan from vision to launch without losing focus.',
      feature2: 'Data-Driven Decisions',
      feature2Desc: 'Validate your ideas with real market feedback before building the product.',
      feature3: 'Expert Mentoring',
      feature3Desc: 'Get 1:1 guidance from experienced founders who have been there.',
      usp: {
        badge: 'Co-Creation & Mentoring',
        title: 'Your fast track to a successful launch',
        column1: {
          badge: 'Mentoring',
          title: 'Consulting Sessions',
          subtitle: 'Expert Guidance',
          desc: 'Get personal 1:1 mentoring to refine your strategy, overcome roadblocks, and accelerate your progress with actionable feedback.'
        },
        column2: {
          badge: 'Build',
          title: 'Co-Creation Build',
          subtitle: 'Technical Execution',
          desc: 'We build your MVP together. Watch your idea turn into a production-ready application in live collaborative sessions.'
        }
      }
    },
    problem: {
      title: 'Lost in the Founder Chaos?',
      subtitle: 'Most founders fail not because of bad ideas, but because they lose focus in the execution phase.',
      chaosLabel: 'The Founder Chaos',
      clarityLabel: 'The Clear Path',
      overwhelmedTitle: 'Overwhelmed by decisions?',
      overwhelmedDesc: 'Too many marketing channels, conflicting advice, and endless feature ideas lead to analysis paralysis.',
      solutionTitle: 'Focus on what matters',
      solutionDesc: 'Our methodology forces you to focus on the 20% of actions that drive 80% of the results.',
      testimonialsTitle: 'From Chaos to Clarity',
      testimonialsSubtitle: 'See how others have transformed their chaotic ideas into structured, growing businesses.',
      methodologySubtitle: 'The 10 Steps to Your Success',
      testimonials: [
        {
          before: "I had 100 ideas but didn't know where to start. I was completely paralyzed.",
          after: 'The structured steps gave me exactly the focus I needed to launch within 6 weeks.',
          name: 'Sarah M.',
          role: 'SaaS Founder'
        },
        {
          before: 'We built a product for 6 months without ever talking to a customer.',
          after: 'Step 2 forced us to validate. We pivoted and got our first paying customers in days.',
          name: 'Thomas K.',
          role: 'E-commerce'
        },
        {
          before: 'Marketing felt like gambling. We wasted thousands on ads with zero ROI.',
          after: 'The strategy canvas helped us nail our messaging. CAC dropped by 70%.',
          name: 'Julia R.',
          role: 'Agency Owner'
        }
      ]
    },
    overview: {
      title: 'The Proven 10-Step Framework',
      subtitle: 'Follow our structured methodology to turn your idea into a successful business.',
      step: 'Step',
      learnMore: 'Learn More'
    },
    differentiation: {
      saved: 'Saved successfully!',
      title: 'Blue Ocean Strategy',
      subtitle: 'Find your uncontested market space and make the competition irrelevant.',
      process1: 'Current State',
      process1Desc: 'Map the current industry standards',
      process2: 'Analyze',
      process2Desc: 'Identify pain points and over-served areas',
      process3: 'ERRC Grid',
      process3Desc: 'Eliminate, Reduce, Raise, Create',
      process4: 'New Curve',
      process4Desc: 'Plot your new value curve',
      loginRequired: 'Login Required',
      errcTitle: 'The Four Actions Framework',
      errcSubtitle: 'Reconstruct buyer value elements to break the value-cost trade-off.',
      eliminate: 'Eliminate',
      eliminateDesc: 'Which factors that the industry takes for granted should be eliminated?',
      reduce: 'Reduce',
      reduceDesc: 'Which factors should be reduced well below the industry standard?',
      raise: 'Raise',
      raiseDesc: 'Which factors should be raised well above the industry standard?',
      create: 'Create',
      createDesc: 'Which factors should be created that the industry has never offered?'
    },
    contact: {
      title: 'Get in Touch',
      subtitle: 'Have questions about our methodology or need custom mentoring? We are here to help.',
      name: 'Your Name',
      email: 'Email Address',
      subject: 'Subject',
      message: 'Message',
      gdpr: 'I agree to the processing of my data according to the privacy policy.',
      submit: 'Send Message',
      success: 'Message sent successfully!',
      infoTitle: 'Contact Information',
      emailLabel: 'Email us',
      workingHours: 'Mon-Fri, 9am - 6pm CET',
      locationLabel: 'Visit us'
    },
    footer: {
      desc: 'Transforming ideas into successful businesses through structured methodology and expert guidance.',
      quickLinks: 'Quick Links',
      contact: 'Contact',
      rights: '© 2024 Stup-On. All rights reserved.',
      privacy: 'Privacy Policy',
      terms: 'Terms of Service',
      cookies: 'Cookie Settings',
      legal: 'Legal Notice'
    },
    auth: {
      title: 'Create Account',
      subtitle: 'Join today and build your success story.',
      email: 'Email Address',
      password: 'Password',
      submit: 'Start for free',
      alreadyHaveAccount: 'Already have an account? Sign In',
      noAccount: "Don't have an account? Sign Up",
      loginTitle: 'Welcome Back',
      loginSubtitle: 'Sign in to continue your journey.',
      userExistsError: 'An account with this email already exists. Please log in instead.'
    },
    welcome: {
      title: 'Welcome to Stup-On!',
      subtitle: 'Congratulations on your successful registration. You are now ready to transform your idea into a market-ready product.',
      cta: 'Go to 10-Step Guide',
      benefits: [
        { title: 'Full Methodology Access', description: 'Unlock all 10 steps of the Stup-On framework.' },
        { title: 'Interactive Tools', description: 'Use our Blue Ocean Strategy Canvas and Vision builders.' },
        { title: 'Premium Support', description: 'Priority access to our mentoring and co-creation sessions.' }
      ],
      premiumStatus: {
        title: 'Premium Access Activated',
        description: 'You have permanent access to all current and future methodology features.'
      },
      footer: 'You can access this guide anytime from your dashboard.'
    },
    detail: {
      back: 'Back to Overview',
      stepOf: 'Step {current} of 10',
      overview: 'Overview',
      actionItems: 'Your Action Items',
      keyFocus: 'Key Focus Points',
      continueTo: 'Continue to Step',
      nextStep: 'Next Step',
      prevStep: 'Previous Step',
      step1IntroTitle: 'Everything is created twice:',
      step1DropdownTitle: 'Why you need a vision?',
      step1DropdownItems: [
        { 
          title: '1. First in your mind', 
          content: 'Before anything exists in the real world, it must first be conceived as a thought, a vision, in your mind. You imagine what your life will look like in 5 years, what successes you will have achieved, and what business you will have built.' 
        },
        { 
          title: '2. Then in reality', 
          content: 'Only after you have developed this clear vision can you put it into practice through concrete action. The vision is the blueprint for your future self and your future business.' 
        }
      ],
      step1ToolTitle: 'Your Vision Tool',
      step1VisionQuestion: "You meet an old school friend in the future, let's say 5 years from now, and he asks you: 'Hey, how are you and how's your life going?'",
      step1VisionLeadIn: "You answer: 'It's going great, it couldn't be better, because...'",
      step1Placeholder: 'Describe your vision here...',
      step1Save: 'Save Vision',
      step1Saving: 'Saving...',
      step1Saved: 'Saved Successfully!',
      step2ToolTitle: 'Knowledge Mapping',
      step2AddTopic: 'Add Topic',
      step2AddSubtopic: 'Add Subtopic',
      step2TopicPlaceholder: 'Topic title...',
      step2ContentPlaceholder: 'Describe your knowledge for this section...',
      step2SaveAll: 'Save All Changes',
      step2Saving: 'Saving...',
      step2Saved: 'Saved Successfully!',
      step2Empty: 'Start by adding your first topic.',
      step2DeleteTopic: 'Delete section',
      step3ToolTitle: 'Your Idea Tool',
      step3ExplanatoryText: "Your idea can be tested for its chances of success beforehand. For this, it's important to define the criteria your idea should meet. Your idea must solve a specific problem for a defined target group. With your idea, you should take your target group on a journey. How are they doing before, and how are they doing with your idea? Embark on this journey and consider the following questions.",
      step3Question1: 'What specific problem does your idea solve?',
      step3Question2: 'For whom (target group) do you solve this?',
      step3Question3: 'What need (for the customer) does your idea fulfill?',
      step3Question4: 'Why are you suitable to solve this?',
      step3Question5: 'What is the product name and slogan?',
      step3Placeholder: 'Type your thoughts here...',
      step3Save: 'Save Idea',
      step3Saving: 'Saving...',
      step3Saved: 'Saved Successfully!',
      step4ToolTitle: 'Customer Avatar Tool',
      step4ExplanatoryText: "A precise understanding of your target audience is crucial; only then can you understand which problem you should solve to build a successful business. Therefore, create your customer avatars here. Put yourself in your customers' shoes: what motivates them, what challenges do they face? You'll quickly realize that the possibilities are virtually endless.",
      step4AddAvatar: 'Add New Avatar',
      step4AvatarName: 'Avatar Name',
      step4Demographics: 'Demographics (Age, Job, Location)',
      step4Motivations: 'Motivations & Goals',
      step4Challenges: 'Challenges & Pain Points',
      step4Save: 'Save Avatar',
      step4Saving: 'Saving...',
      step4Saved: 'Saved Successfully!',
      step4Delete: 'Delete Avatar',
      step5ToolTitle: 'Blue Ocean Strategy Tool',
      step5Intro: "The Blue Ocean Strategy is not just about being better than the competition, but about being different. It's about finding an uncontested market space where the competition becomes irrelevant. Instead of competing in an overcrowded market ('Red Ocean'), you create a new market ('Blue Ocean') by offering unique value. Use our Niche Finder tool to analyze your current market position and develop your own Blue Ocean Strategy.",
      step5CTA: 'Open Niche Finder',
      step7ToolTitle: 'Offer Formulation Tool',
      step7ExplanatoryText: 'When formulating your offer, it is important to describe your product, your target group, the goal that will be achieved, and the shortcut to achieving it as precisely as possible. Furthermore, you must show your customer how they feel on their journey, first without your product and then with your product.',
      step7PerfectOfferTitle: 'The Perfect Offer Formula',
      step7PerfectOfferPhrase: '(PRODUCT) helps (TARGET GROUP) achieve (GOAL) without (CONSIDERING)',
      step7LabelProduct: 'Product Description',
      step7LabelTarget: 'Target Group',
      step7LabelGoal: 'Achievable Goal',
      step7LabelShortcut: 'The Shortcut',
      step7PlaceholderProduct: '(PRODUCT) helps (TARGET GROUP) achieve (GOAL) without (CONSIDERATIONS).',
      step7PlaceholderTarget: 'Describe your target group (age, gender, position, location) as precisely as possible.',
      step7PlaceholderGoal: 'What goal will the customer achieve with the product?',
      step7PlaceholderShortcut: 'What shortcut does the product offer?',
      step7JourneyTitle: 'The Emotional Journey',
      step7JourneyBefore: 'Before: How do they feel WITHOUT your product?',
      step7JourneyAfter: 'After: How do they feel WITH your product?',
      step7Save: 'Save Offer',
      step7Saving: 'Saving...',
      step7Saved: 'Saved Successfully!',
      step9ToolTitle: 'MVP Scope & Roadmap',
      step9ExplanatoryText: 'Congratulations on successfully completing all steps up to this point. This is an important prerequisite for testing the product in practice. Rapid testing is essential for the feedback loop and represents a crucial step.',
      step9WebinarTitle: 'Free MVP Masterclass',
      step9WebinarDesc: 'Join our free webinar to learn how to quickly build an online MVP and effectively test your product in the market. Learn from experienced founders and avoid common pitfalls.',
      step9WebinarCTA: 'Register for Webinar',
      step9WebinarSuccess: 'Successfully registered for the webinar!',
      step9AddFeature: 'Add MVP Feature',
      step9FeatureName: 'Feature Name / Description',
      step9Priority: 'Priority',
      step9Status: 'Status',
      step9Save: 'Save MVP Plan',
      step9Saving: 'Saving...',
      step9Saved: 'Saved Successfully!',
      step9Delete: 'Delete Item',
      lockedTitle: 'Content Locked',
      lockedDesc: 'Unlock premium to access the full potential of this step.',
      unlockNow: 'Unlock Now',
      alreadyPurchased: 'Already purchased?',
      authPrompt: 'Please log in or register with the email address used for your purchase to access your premium content.',
      login: 'Log In',
      register: 'Register',
      step5Low: 'Low',
      step5Medium: 'Medium',
      step5High: 'High',
      step5UseCase: 'Use Case / Target Audience',
      step5Placeholder: 'Describe the specific use case or target audience for this strategy...',
      step5Elements: 'Value Factors',
      step5Curve1: 'Your Offering',
      step5Curve2: 'Industry Standard',
      step5AddElement: 'Add Value Factor',
      step5Save: 'Save Strategy',
      step5ChartTitle: 'Strategy Canvas'
    },
    paths: {
      home: '/',
      steps: '/steps',
      differentiation: '/find-your-niche',
      contact: '/contact',
      login: '/login',
      register: '/register',
      privacy: '/privacy',
      terms: '/terms',
      cookies: '/cookies',
      legal: '/legal-notice',
      welcome: '/welcome'
    }
  },
  de: {
    nav: {
      home: 'Start',
      steps: 'Schritte',
      differentiation: 'Nischen-Finder',
      contact: 'Kontakt',
      login: 'Anmelden',
      logout: 'Abmelden',
      getStarted: 'Kostenlos starten'
    },
    hero: {
      subtitle: '10 einfache Schritte und eine strukturierte Anleitung, die dich von deiner Idee zum fertigen Geschäftsmodell begleitet. Von der Vision zur Zielgruppe über die technische Umsetzung bis hin zum Marketing - gebündelt an einem Ort und perfekt aufeinander abgestimmt',
      subtitleHighlight: 'Mit Stup-On bleibst du am Ball und baust ganz nebenbei dein eigenes Business auf 🚀.',
      ctaPrimary: 'Kostenlos starten',
      ctaSecondary: 'Mehr entdecken',
      feature1: 'Strukturierter Prozess',
      feature1Desc: 'Folge dem bewährten 10-Schritte-Plan von der Vision bis zum Launch, ohne dabei den Fokus zu verlieren.',
      feature2: 'Triff die richtigen Entscheidungen',
      feature2Desc: 'Validiere deine Ideen, bevor du das Produkt baust. Setzte die richtigen Prioritäten und schau dir an ob deine Idee funktioniert',
      feature3: 'Experten-Mentoring',
      feature3Desc: 'Erhalte bei Bedarf eine 1:1 Betreuung und baue dein erstes Online-Produkt - maßgeschädt für deine Zielgruppe.',
      usp: {
        badge: 'Co-Creation & Mentoring',
        title: 'Dein Überholweg zum erfolgreichen Launch',
        column1: {
          badge: 'Mentoring',
          title: 'Consulting Sessions',
          subtitle: 'Experten-Betreuung',
          desc: 'Erhalte dein persönliches 1:1 Mentoring, um deine Strategie zu schärfen, Hindernisse zu überwinden und deinen Fortschritt mit umsetzbarem Feedback zu beschleunigen.'
        },
        column2: {
          badge: 'Build',
          title: 'Co-Creation Build',
          subtitle: 'Technische Umsetzung',
          desc: 'Wir bauen dein MVP gemeinsam. Erlebe, wie deine Idee in gemeinsamen Live-Sessions zu einer produktionsreifen Anwendung wird.'
        }
      }
    },
    problem: {
      title: 'Verloren im Gründer-Chaos?',
      subtitle: 'Die meisten Gründer scheitern nicht an schlechten Ideen, sondern weil sie in der Umsetzungsphase den Fokus verlieren.',
      chaosLabel: 'Das Gründer-Chaos',
      clarityLabel: 'Das Geradlinige',
      overwhelmedTitle: 'Überwältigt von Entscheidungen?',
      overwhelmedDesc: 'Zu viele Marketingkanäle, widersprüchliche Ratschläge und endlose Feature-Ideen führen zur Überforderung.',
      solutionTitle: 'Fokus auf das Wesentliche',
      solutionDesc: 'Unsere Methodik zwingt dich, dich auf die 20% der Aktionen zu konzentrieren, die 80% der Ergebnisse bringen.',
      testimonialsTitle: 'Vom Chaos zur Klarheit',
      testimonialsSubtitle: 'Sieh, wie andere ihre chaotischen Ideen in strukturierte, wachsende Unternehmen verwandelt haben.',
      methodologySubtitle: 'The 10 Schritte zu deinem Erfolg',
      testimonials: [
        {
          before: 'Ich hatte 100 Ideen, wusste aber nicht, wo ich anfangen sollte. Ich war komplett blockiert.',
          after: 'Die strukturierten Schritte gaben mir genau den Fokus, den ich brauchte, um in 6 Wochen zu launchen.',
          name: 'Sarah M.',
          role: 'SaaS-Gründerin'
        },
        {
          before: 'Wir haben 6 Monate lang ein Produkt gebaut, ohne je mit einem Kunden zu sprechen.',
          after: 'Schritt 2 zwang uns zur Validierung. Wir haben den Kurs geändert und in Tagen zahlende Kunden gewonnen.',
          name: 'Thomas K.',
          role: 'E-Commerce'
        },
        {
          before: 'Marketing fiel wie Glücksspiel an. Wir haben Tausende für Ads ohne ROI verschwendet.',
          after: 'Das Strategie-Canvas half uns, unsere Botschaft auf den Punkt zu bringen. CAC sank um 70%.',
          name: 'Julia R.',
          role: 'Agentur-Inhaberin'
        }
      ]
    },
    overview: {
      title: 'Das bewährte 10-Schritte-System',
      subtitle: 'Folge unserer strukturierten Methodik, um deine Idee in ein erfolgreiches Unternehmen zu verwandeln.',
      step: 'Schritt',
      learnMore: 'Mehr erfahren'
    },
    differentiation: {
      saved: 'Erfolgreich gespeichert!',
      title: 'Blue Ocean Strategie',
      subtitle: 'Finde deinen unangetasteten Markt und mache die Konkurrenz irrelevant.',
      process1: 'Ist-Zustand',
      process1Desc: 'Erfasse die aktuellen Branchenstandards',
      process2: 'Analysieren',
      process2Desc: 'Identifiziere Schmerzpunkte und überversorgte Bereiche',
      process3: 'ERRC-Raster',
      process3Desc: 'Eliminieren, Reduzieren, Steigern, Kreieren',
      process4: 'Neue Kurve',
      process4Desc: 'Zeichne deine neue Nutzenkurve',
      loginRequired: 'Anmeldung erforderlich',
      errcTitle: 'Das Vier-Aktionen-Format',
      errcSubtitle: 'Rekonstruiere die Nutzen-Elemente, um den Trade-off zwischen Wert und Kosten zu durchbrechen.',
      eliminate: 'Eliminieren',
      eliminateDesc: 'Welche Faktoren, die die Branche als selbstverständlich ansieht, sollten eliminiert werden?',
      reduce: 'Reduzieren',
      reduceDesc: 'Welche Faktoren sollten weit unter den Branchenstandard reduziert werden?',
      raise: 'Steigern',
      raiseDesc: 'Welche Faktoren sollten weit über den Branchenstandard gesteigert werden?',
      create: 'Kreieren',
      createDesc: 'Welche Faktoren sollten kreiert werden, die die Branche noch nie angeboten hat?'
    },
    contact: {
      title: 'Kontakt aufnehmen',
      subtitle: 'Hast du Fragen zu unserer Methodik oder benötigst du individuelles Mentoring? Wir sind hier, um zu helfen.',
      name: 'Dein Name',
      email: 'E-Mail-Adresse',
      subject: 'Betreff',
      message: 'Nachricht',
      gdpr: 'Ich stimme der Verarbeitung meiner Daten gemäß der Datenschutzerklärung zu.',
      submit: 'Nachricht senden',
      success: 'Nachricht erfolgreich gesendet!',
      infoTitle: 'Kontaktinformationen',
      emailLabel: 'Schreib uns',
      workingHours: 'Mo-Fr, 09:00 - 18:00 MEZ',
      locationLabel: 'Besuch uns'
    },
    footer: {
      desc: 'Wir verwandeln Ideen durch strukturierte Methodik und Expertenbegleitung in erfolgreiche Unternehmen.',
      quickLinks: 'Schnelllinks',
      contact: 'Kontakt',
      rights: '© 2024 Stup-On. All rights reserved.',
      privacy: 'Datenschutz',
      terms: 'AGB',
      cookies: 'Cookie-Einstellungen',
      legal: 'Impressum'
    },
    auth: {
      title: 'Konto erstellen',
      subtitle: 'Werde Teil der Erfolgsspur und baue dein Business auf.',
      email: 'E-Mail-Adresse',
      password: 'Passwort',
      submit: 'Kostenlos starten',
      alreadyHaveAccount: 'Bereits ein Konto? Anmelden',
      noAccount: 'Noch kein Konto? Registrieren',
      loginTitle: 'Willkommen zurück',
      loginSubtitle: 'Melde dich an, um fortzufahren.',
      userExistsError: 'Ein Konto mit dieser E-Mail existiert bereits. Bitte melde dich stattdessen an.'
    },
    welcome: {
      title: 'Willkommen bei Stup-On!',
      subtitle: 'Herzlichen Glückwunsch zu deiner erfolgreichen Registrierung. Du bist jetzt bereit, deine Idee in ein marktreifes Produkt zu verwandeln.',
      cta: 'Zum 10-Schritte-Guide',
      benefits: [
        { title: 'Voller Zugriff auf die Methodik', description: 'Schalte alle 10 Schritte des Stup-On-Frameworks frei.' },
        { title: 'Interaktive Tools', description: 'Nutze unser Blue Ocean Strategy Canvas and Vision-Builder.' },
        { title: 'Premium-Support', description: 'Bevorzugter Zugang zu unseren Mentoring- und Co-Creation-Sessions.' }
      ],
      premiumStatus: {
        title: 'Premium-Zugang aktiviert',
        description: 'Du hast dauerhaften Zugriff auf alle aktuellen und zukünftigen Funktionen.'
      },
      footer: 'Du kannst diesen Guide jederzeit über dein Dashboard aufrufen.'
    },
    detail: {
      back: 'Zurück zur Übersicht',
      stepOf: 'Schritt {current} von 10',
      overview: 'Überblick',
      actionItems: 'Deine Aufgaben',
      keyFocus: 'Fokus-Punkte',
      continueTo: 'Weiter zu Schritt',
      nextStep: 'Nächster Schritt',
      prevStep: 'Vorheriger Schritt',
      step1IntroTitle: 'Alles wird zweimal erschaffen:',
      step1DropdownTitle: 'Warum brauchst du eine Vision?',
      step1DropdownItems: [
        { 
          title: '1. Zuerst in deinem Kopf', 
          content: 'Bevor etwas in der realen Welt existiert, muss es zuerst als Gedanke, als Vision, in deinem Kopf erdacht werden. Du stellst dir vor, wie dein Leben in 5 Jahren aussehen wird, welche Erfolge du erzielt hast und welches Unternehmen du aufgebaut hast.' 
        },
        { 
          title: '2. Dann in der Realität', 
          content: 'Erst nachdem du diese klare Vision entwickelt hast, kannst du sie durch konkretes Handeln in die Tat umsetzen. Die Vision ist der Bauplan für dein zukünftiges Ich und dein zukünftiges Unternehmen.' 
        }
      ],
      step1ToolTitle: 'Dein Visions-Tool',
      step1VisionQuestion: "Du triffst in der Zukunft einen alten Schulfreund, sagen wir in 5 Jahren, und er fragt dich: 'Hey, wie geht es dir und wie läuft dein Leben?'",
      step1VisionLeadIn: "Du antwortest: 'Es läuft super, es könnte nicht besser sein, weil...'",
      step1Placeholder: 'Beschreibe hier deine Vision...',
      step1Save: 'Vision speichern',
      step1Saving: 'Speichert...',
      step1Saved: 'Erfolgreich gespeichert!',
      step2ToolTitle: 'Wissens-Mapping',
      step2AddTopic: 'Thema hinzufügen',
      step2AddSubtopic: 'Unterthema hinzufügen',
      step2TopicPlaceholder: 'Titel des Themas...',
      step2ContentPlaceholder: 'Beschreibe dein Wissen für diesen Abschnitt...',
      step2SaveAll: 'Alles speichern',
      step2Saving: 'Speichert...',
      step2Saved: 'Erfolgreich gespeichert!',
      step2Empty: 'Beginne mit deinem ersten Thema.',
      step2DeleteTopic: 'Abschnitt löschen',
      step3ToolTitle: 'Dein Ideen-Tool',
      step3ExplanatoryText: "Deine Idee kann vorab auf ihre Erfolgsaussichten geprüft werden. Hierfür ist es wichtig, die Kriterien zu definieren, die deine Idee erfüllen soll. Deine Idee muss ein spezifisches Problem für eine definierte Zielgruppe lösen. Mit deiner Idee solltest du deine Zielgruppe auf eine Reise mitnehmen. Wie geht es ihnen vorher und wie geht es ihnen mit deiner Idee? Begib dich auf diese Reise und berücksichtige die folgenden Fragen.",
      step3Question1: 'Welches spezifische Problem löst deine Idee?',
      step3Question2: 'Für wen (Zielgruppe) löst du dieses Problem?',
      step3Question3: 'Welches Bedürfnis (für den Kunden) erfüllt deine Idee?',
      step3Question4: 'Warum bist du geeignet, dieses Problem zu lösen?',
      step3Question5: 'Wie soll dein Produkt heißen und welcher Slogan wird genutzt?',
      step3Placeholder: 'Schreibe hier deine Gedanken...',
      step3Save: 'Idee speichern',
      step3Saving: 'Speichert...',
      step3Saved: 'Erfolgreich gespeichert!',
      step3Empty: 'Starte mit deinem ersten Thema.',
      step4ToolTitle: 'Kunden-Avatar-Tool',
      step4ExplanatoryText: "Ein präzises Verständnis deiner Zielgruppe ist entscheidend; nur dann kannst du verstehen, welches Problem du lösen solltest, um ein erfolgreiches Unternehmen aufzubauen. Erstelle daher hier deine Kunden-Avatare. Versetze dich in die Lage deiner Kunden: Was motiviert sie, vor welchen Herausforderungen stehen sie? Du wirst schnell merken, dass die Möglichkeiten quasi unendlich sind.",
      step4AddAvatar: 'Neuen Avatar hinzufügen',
      step4AvatarName: 'Name des Avatars',
      step4Demographics: 'Demografie (Alter, Beruf, Ort)',
      step4Motivations: 'Motivation & Ziele',
      step4Challenges: 'Herausforderungen & Sorgen',
      step4Save: 'Avatar speichern',
      step4Saving: 'Speichert...',
      step4Saved: 'Erfolgreich gespeichert!',
      step4Delete: 'Avatar löschen',
      step5ToolTitle: 'Blue Ocean Strategie Tool',
      step5Intro: "Bei der Blue-Ocean-Strategie geht es nicht darum, besser als die Konkurrenz zu sein, sondern darum, anders zu sein. Es geht darum, einen unbesetzten Marktraum zu finden, in dem die Konkurrenz irrelevant wird. Anstatt in einem überfüllten Markt ('Roter Ozean') zu konkurrieren, erschaffen Sie durch einzigartige Wertangebote einen neuen Markt ('Blauer Ozean'). Nutzen Sie unser Nischen-Finder-Tool, um Ihre aktuelle Marktposition zu analysieren und Ihre eigene Blue-Ocean-Strategie zu entwickeln.",
      step5CTA: 'Nischen-Finder öffnen',
      step7ToolTitle: 'Angebots-Tool',
      step7ExplanatoryText: 'Bei der Formulierung deines Angebots ist es wichtig, dein Produkt, deine Zielgruppe, das zu erreichende Ziel und die Abkürzung dorthin so präzise wie möglich zu beschreiben. Außerdem musst du deinem Kunden zeigen, wie er sich auf seiner Reise fühlt, erst ohne dein Produkt und dann mit dein Produkt.',
      step7PerfectOfferTitle: 'Die perfekte Angebots-Formel',
      step7PerfectOfferPhrase: '(PRODUKT) hilft (ZIELGRUPPE), (ZIEL) zu erreichen, ohne (BEDENKEN/HÜRDEN).',
      step7LabelProduct: 'Produktbeschreibung',
      step7LabelTarget: 'Zielgruppe',
      step7LabelGoal: 'Erreichbares Ziel',
      step7LabelShortcut: 'Die Abkürzung',
      step7PlaceholderProduct: '(PRODUKT) hilft (ZIELGRUPPE), (ZIEL) zu erreichen, ohne (BEDENKEN/HÜRDEN).',
      step7PlaceholderTarget: 'Beschreibe deine Zielgruppe (Alter, Geschlecht, Position, Ort) so präze wie möglich.',
      step7PlaceholderGoal: 'Welches Ziel wird der Kunde mit dem Produkt erreichen?',
      step7PlaceholderShortcut: 'Welche Abkürzung bietet das Produkt?',
      step7JourneyTitle: 'Die emotionale Reise',
      step7JourneyBefore: 'Vorher: Wie fühlen sie sich OHNE dein Produkt?',
      step7JourneyAfter: 'Nachher: Wie fühlen sie sich MIT deinem Produkt?',
      step7Save: 'Angebot speichern',
      step7Saving: 'Speichert...',
      step7Saved: 'Erfolgreich gespeichert!',
      step9ToolTitle: 'MVP-Umfang & Roadmap',
      step9ExplanatoryText: 'Herzlichen Glückwunsch zum erfolgreichen Abschluss aller bisherigen Schritte. Dies ist eine wichtige Voraussetzung, um das Produkt in der Praxis zu testen. Schnelles Testen ist für den Feedback-Loop unerlässlich und stellt einen entscheidenden Schritt dar.',
      step9WebinarTitle: 'Kostenlose MVP-Masterclass',
      step9WebinarDesc: 'Nimm an unserem kostenlosen Webinar teil und lerne, wie du schnell ein Online-MVP erstellst und dein Produkt effektiv am Markt testest. Lerne von erfahrenen Gründern und vermeide die typischen Fehler.',
      step9WebinarCTA: 'Zum Webinar anmelden',
      step9WebinarSuccess: 'Erfolgreich zum Webinar angemeldet!',
      step9AddFeature: 'MVP-Funktion hinzufügen',
      step9FeatureName: 'Funktion / Beschreibung',
      step9Priority: 'Priorität',
      step9Status: 'Status',
      step9Save: 'MVP-Plan speichern',
      step9Saving: 'Speichert...',
      step9Saved: 'Erfolgreich gespeichert!',
      step9Delete: 'Eintrag löschen',
      lockedTitle: 'Inhalt gesperrt',
      lockedDesc: 'Schalte Premium frei, um das volle Potenzial dieses Schrittes zu nutzen.',
      unlockNow: 'Jetzt freischalten',
      alreadyPurchased: 'Bereits gekauft?',
      authPrompt: 'Bitte melde dich an oder registriere dich mit der E-Mail-Adresse, die du für den Kauf verwendet hast, um auf deine Premium-Inhalte zuzugreifen.',
      login: 'Anmelden',
      register: 'Registrieren',
      step5Low: 'Niedrig',
      step5Medium: 'Mittel',
      step5High: 'Hoch',
      step5UseCase: 'Anwendungsfall / Zielgruppe',
      step5Placeholder: 'Beschreibe den spezifischen Anwendungsfall oder die Zielgruppe für diese Strategie...',
      step5Elements: 'Wertfaktoren',
      step5Curve1: 'Dein Angebot',
      step5Curve2: 'Branchenstandard',
      step5AddElement: 'Wertfaktor hinzufügen',
      step5Save: 'Strategie speichern',
      step5ChartTitle: 'Strategie-Canvas'
    },
    paths: {
      home: '/',
      steps: '/schritte',
      differentiation: '/finde-deine-Nische',
      contact: '/kontakt',
      login: '/anmelden',
      register: '/registrieren',
      privacy: '/datenschutz',
      terms: '/agb',
      cookies: '/cookies',
      legal: '/impressum',
      welcome: '/willkommen'
    }
  }
};

export const stepsTranslations = {
  en: [
    {
      id: 1,
      title: "Your Vision",
      quote: "Vision is the art of seeing things invisible.",
      quoteAuthor: "Jonathan Swift",
      description: "Everything is created twice: first in your mind, then in reality. Define the blueprint for your future business.",
      subtitle: "Define your core problem and create a compelling vision for the future.",
      content: "Your vision is your North Star. Before anything exists in the real world, it must first be conceived as a thought in your mind.",
      keyPoints: ["5-Year Vision", "Mental Blueprint", "Founder Purpose", "Future Success"],
      actionItems: ["Clearly articulate your 5-year future scenario", "Identify the core achievements that make it 'perfect'"]
    },
    {
      id: 2,
      title: "Your Knowledge",
      quote: "Knowledge is power.",
      quoteAuthor: "Francis Bacon",
      description: "Understand your domain and leverage your unique expertise.",
      subtitle: "Map out your existing knowledge and identify where you need to learn more.",
      content: "Success starts with what you know. We'll help you audit your skills and market understanding.",
      keyPoints: ["Domain Expertise", "Skill Audit", "Industry Insights", "Learning Path"],
      actionItems: ["List your top 5 technical skills", "Identify 3 gaps in your market knowledge"]
    },
    {
      id: 3,
      title: "Your Idea",
      quote: "The best way to have a good idea is to have a lot of ideas.",
      quoteAuthor: "Linus Pauling",
      description: "Brainstorm and evaluate potential solutions to the problem you identified.",
      subtitle: "Brainstorm and evaluate potential solutions to your identified problem.",
      content: "Now it's time to generate as many ideas as possible. The goal is quantity, which we will later filter for quality.",
      keyPoints: ["Brainstorming Techniques", "Idea Filtering", "Feasibility Check", "Impact vs. Effort"],
      actionItems: ["Generate 20 different ways to solve the problem", "Group similar ideas into core concepts"]
    },
    {
      id: 4,
      title: "Your Target Audience",
      quote: "Don't find customers for your products, find products for your customers.",
      quoteAuthor: "Seth Godin",
      description: "Deeply understand who you are building for and what they really need.",
      subtitle: "Define your ideal customer and validate their pain points.",
      content: "Building for everyone is building for no one. We'll narrow down your focus to a specific niche.",
      keyPoints: ["User Personas", "Pain Point Analysis", "Customer Interviews", "Behavioral Patterns"],
      actionItems: ["Create a detailed persona of your early adopter", "Interview 5 potential users"]
    },
    {
      id: 5,
      title: "Find Your Niche",
      quote: "Be a big fish in a small pond.",
      quoteAuthor: "Proverb",
      description: "Identify the specific market segment where you can dominate.",
      subtitle: "Visualize your strategy and break away from the competition.",
      content: "A niche allows you to become the authority quickly. We'll find the uncontested market space.",
      keyPoints: ["Market Segmentation", "Blue Ocean Strategy", "Competitive Analysis", "Focus Area"],
      actionItems: ["Map your value curve against competitors", "Identify 3 over-served market areas"]
    },
    {
      id: 6,
      title: "Create Relevance in Your Niche",
      quote: "Relevance is the new currency.",
      quoteAuthor: "Anonymous",
      description: "Ensure your solution is exactly what your niche is looking for.",
      subtitle: "Tailor your messaging and features to speak directly to your niche.",
      content: "Resonance is key. We'll refine your positioning to ensure it hits home with your target group.",
      keyPoints: ["Positioning", "Messaging Hierarchy", "Value Alignment", "Niche Resonance"],
      actionItems: ["Draft your core value proposition", "Test messaging with your target audience"]
    },
    {
      id: 7,
      title: "Formulate Your Offer",
      quote: "Make them an offer they can't refuse.",
      quoteAuthor: "Mario Puzo",
      description: "Package your solution into a compelling product or service.",
      subtitle: "Define exactly what you are selling and how it's priced.",
      content: "Your offer is more than just a product. It's the total value package you provide to the customer.",
      keyPoints: ["Product Packaging", "Pricing Strategy", "Incentives", "Risk Reversal"],
      actionItems: ["Define your MVP feature set", "Set your initial price point"]
    },
    {
      id: 8,
      title: "Build Trust Through Your Content",
      quote: "Content builds relationships. Relationships are built on trust. Trust drives revenue.",
      quoteAuthor: "Andrew Davis",
      description: "Create a content strategy that establishes you as an authority.",
      subtitle: "Demonstrate your expertise and build a connection with your audience.",
      content: "Trust is earned through consistent value. We'll plan your content to educate and inspire.",
      keyPoints: ["Content Strategy", "Educational Marketing", "Case Studies", "Social Proof"],
      actionItems: ["Plan 4 weeks of educational content", "Identify 2 case study opportunities"]
    },
    {
      id: 9,
      title: "Co-Create MVP",
      quote: "If you are not embarrassed by the first version of your product, you've launched too late.",
      quoteAuthor: "Reid Hoffman",
      description: "Congratulations on successfully completing all steps up to this point. This is an important prerequisite for testing the product in practice.",
      subtitle: "Get a functional version of your idea into the hands of real users.",
      content: "Rapid testing is essential for the feedback loop and represents a crucial step. Don't build in isolation. We'll help you focus on the core functionality that delivers value.",
      keyPoints: ["Agile Development", "User Feedback Loops", "Core Functionality", "Rapid Iteration"],
      actionItems: ["Build the core feature of your MVP", "Set up a feedback system"]
    },
    {
      id: 10,
      title: "Launch Plan",
      quote: "Execution is everything.",
      quoteAuthor: "John Doerr",
      description: "Prepare everything for a successful go-to-market execution.",
      subtitle: "Create a roadmap for your official market entry.",
      content: "A great product needs a great launch. We'll plan the steps to ensure maximum impact.",
      keyPoints: ["Go-to-Market Strategy", "Launch Timeline", "Promotion Plan", "KPI Tracking"],
      actionItems: ["Set your launch date", "Create a pre-launch buzz campaign"]
    }
  ],
  de: [
    {
      id: 1,
      title: "Deine Vision",
      quote: "Vision ist die Kunst, Unsichtbares zu sehen.",
      quoteAuthor: "Jonathan Swift",
      description: "Alles wird zweimal erschaffen: Erst in deinem Kopf, und dann in der Realität. Definiere den Bauplan für dein zukünftiges Unternehmen.",
      subtitle: "Definiere dein Kernproblem und erschaffe eine überzeugende Zukunftsvision.",
      content: "Deine Vision ist dein Nordstern. Bevor etwas in der realen Welt existiert, muss es zuerst als Gedanke in deinem Kopf erdacht werden.",
      keyPoints: ["5-Jahres-Vision", "Mentaler Bauplan", "Gründer-Zweck", "Zukünftiger Erfolg"],
      actionItems: ["Formuliere dein 5-Jahres-Zukunftsszenario", "Identifiziere die Erfolge, die es 'perfekt' machen"]
    },
    {
      id: 2,
      title: "Dein Wissen",
      quote: "Wissen ist Macht.",
      quoteAuthor: "Francis Bacon",
      description: "Verstehe deine Wissens-Domäne und nutze dein einzigartiges Fachwissen.",
      subtitle: "Erfasse dein vorhandenes Wissen und identifiziere Lernfelder.",
      content: "Erfolg beginnt mit dem, was du weißt. Wir helfen dir, deine Fähigkeiten und dein Marktverständnis zu prüfen.",
      keyPoints: ["Fachexpertise", "Skill-Audit", "Branchen-Insights", "Lernpfad"],
      actionItems: ["Liste deine Top 5 Fähigkeiten auf", "Identifiziere 3 Wissenslücken"]
    },
    {
      id: 3,
      title: "Deine Idee",
      quote: "Der beste Weg, eine gute Idee zu haben, ist, viele Ideen zu haben.",
      quoteAuthor: "Linus Pauling",
      description: "Brainstorme und bewerte potenzielle Lösungen für dein Problem.",
      subtitle: "Sammle und bewerte Ideen zur Lösung deines Kernproblems.",
      content: "Jetzt ist es an der Zeit, so viele Ideen wie möglich zu generieren. Das Ziel ist Quantität.",
      keyPoints: ["Brainstorming-Techniken", "Ideen-Filterung", "Machbarkeitsprüfung", "Impact vs. Aufwand"],
      actionItems: ["Generiere 20 verschiedene Lösungsansätze", "Gruppiere Ideen in Kernkonzepte"]
    },
    {
      id: 4,
      title: "Deine Zielgruppe",
      quote: "Finde nicht Kunden für deine Produkte, finde Produkte für deine Kunden.",
      quoteAuthor: "Seth Godin",
      description: "Verstehe tiefgreifend, für wen du baust und was diese Menschen wirklich brauchen.",
      subtitle: "Definiere deinen idealen Kunden und validiere deren Schmerzpunkte.",
      content: "Wer für alle baut, baut für niemanden. Wir fokussieren uns auf eine spezifische Nische.",
      keyPoints: ["User Personas", "Schmerzpunkt-Analyse", "Kundeninterviews", "Verhaltensmuster"],
      actionItems: ["Erstelle ein detailliertes Profil deines Early Adopters", "Führe 5 Interviews mit potenziellen Nutzern"]
    },
    {
      id: 5,
      title: "Finde deine Nische",
      quote: "Sei ein großer Fisch in einem kleinen Teich.",
      quoteAuthor: "Sprichwort",
      description: "Identifiziere das Marktsegment, in dem du dominieren kannst.",
      subtitle: "Visualisiere deine Strategie und setze dich vom Wettbewerb ab.",
      content: "Eine Nische ermöglicht es dir, schnell zur Autorität zu werden. Wir finden den blauen Ozean.",
      keyPoints: ["Marktsegmentierung", "Blue Ocean Strategie", "Wettbewerbsanalyse", "Fokusbereich"],
      actionItems: ["Zeichne deine Nutzenkurve im Vergleich zum Wettbewerb", "Identifiziere 3 überversorgte Bereiche"]
    },
    {
      id: 6,
      title: "Schaffe Relevanz in deiner Nische",
      quote: "Relevanz ist die neue Währung.",
      quoteAuthor: "Anonym",
      description: "Stelle sicher, dass deine Lösung genau das ist, wonach deine Nische sucht. Denn nur wer heute relevanten Inhalt liefert wird gesehen und hebt sich hervor.",
      subtitle: "Schneide deine Botschaft und Funktionen direkt auf deine Nische zu.",
      content: "Resonanz ist der Schlüssel. Wir verfeinern deine Positionierung für maximale Wirkung.",
      keyPoints: ["Positionierung", "Botschafts-Hierarchie", "Werte-Abgleich", "Nischen-Resonanz"],
      actionItems: ["Entwirf dein Kern-Wertversprechen", "Teste deine Botschaften mit der Zielgruppe"]
    },
    {
      id: 7,
      title: "Formuliere dein Angebot",
      quote: "Mach ihnen ein Angebot, das sie nicht ablehnen können.",
      quoteAuthor: "Mario Puzo",
      description: "Verpacke deine Lösung in ein unwiderstehliches Produkt oder eine Dienstleistung. Hier erfährst du, wie du ein passendes Angebot erzeugst.",
      subtitle: "Definiere genau, was du verkaufst und wie es bepreist wird.",
      content: "Dein Angebot ist mehr als nur ein Produkt. Es ist das gesamte Wertpaket für den Kunden.",
      keyPoints: ["Produktpaketierung", "Pricing Strategy", "Incentives", "Risk Reversal"],
      actionItems: ["Definiere den Funktionsumfang deines MVPs", "Lege deinen ersten Preis fest"]
    },
    {
      id: 8,
      title: "Baue Vertrauen durch deinen Content auf",
      quote: "Content baut Beziehungen auf. Beziehungen bauen auf Vertrauen auf. Vertrauen generiert Umsatz.",
      quoteAuthor: "Andrew Davis",
      description: "Erstelle eine Content-Strategie, die dich als Autorität etabliert.",
      subtitle: "Demonstriere deine Expertise und baue eine Verbindung zu deinem Publikum auf.",
      content: "Vertrauen verdient man sich durch konsanten Mehrwert. Wir planen deinen Bildungs-Content.",
      keyPoints: ["Content-Strategie", "Educational Marketing", "Fallstudien", "Social Proof"],
      actionItems: ["Plane Content für 4 Wochen", "Identifiziere Möglichkeiten für Fallstudien"]
    },
    {
      id: 10,
      title: "Launch-Plan",
      quote: "Ausführung ist alles.",
      quoteAuthor: "John Doerr",
      description: "Bereite alles für eine erfolgreiche Markteinführung vor.",
      subtitle: "Erstelle einen Fahrplan für deinen offiziellen Markteintritt.",
      content: "Ein tolles Produkt braucht einen tollen Launch. Wir planen die Schritte für maximale Wirkung.",
      keyPoints: ["Go-to-Market Strategy", "Launch-Zeitplan", "Promotions-Plan", "KPI-Tracking"],
      actionItems: ["Lege dein Launch-Datum fest", "Erstelle eine Pre-Launch-Kampagne"]
    }
  ]
};